import React, { useState, useEffect } from 'react';

const PayoutManager = ({ tournamentId, onClose }) => {
  const [payouts, setPayouts] = useState([
    { position: '1st', percentage: 50, amount: 0 },
    { position: '2nd', percentage: 30, amount: 0 },
    { position: '3rd', percentage: 20, amount: 0 }
  ]);
  const [prizePool, setPrizePool] = useState(0);
  const [usePercentages, setUsePercentages] = useState(false);

  const getApiUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000';
    } else if (hostname === '10.0.0.28') {
      return `https://${hostname}/api`;
    } else {
      return `https://${hostname}/api`;
    }
  };

  useEffect(() => {
    loadPayouts();
  }, [tournamentId]);

  const loadPayouts = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}`);
      const data = await response.json();
      
      if (data.payout_structure && data.payout_structure.length > 0) {
        setPayouts(data.payout_structure);
      }
      
      // Get current prize pool from clock data
      const clockResponse = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/clock`);
      const clockData = await clockResponse.json();
      setPrizePool(clockData.total_prize_pool || 0);
    } catch (error) {
      console.error('Error loading payouts:', error);
    }
  };

  const addPlace = () => {
    const newPosition = payouts.length + 1;
    const positions = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];
    setPayouts([...payouts, {
      position: positions[newPosition - 1] || `${newPosition}th`,
      percentage: 0,
      amount: 0
    }]);
  };

  const removePlace = (index) => {
    setPayouts(payouts.filter((_, i) => i !== index));
  };

  const updatePayout = (index, field, value) => {
    const updated = [...payouts];

    if (field === 'position' || field === 'label') {
      updated[index][field] = value;
    } else {
      updated[index][field] = parseFloat(value) || 0;

      // If using percentages, calculate amounts
      if (field === 'percentage' && usePercentages && prizePool > 0) {
        updated[index].amount = (prizePool * parseFloat(value) / 100);
      }
    }

    setPayouts(updated);
  };

  const calculateFromPercentages = () => {
    const updated = payouts.map(p => ({
      ...p,
      amount: prizePool * (p.percentage / 100)
    }));
    setPayouts(updated);
  };

const savePayouts = async () => {
  try {
    const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        payout_structure: JSON.stringify(payouts)  // DOUBLE STRINGIFY
      })
    });

    if (response.ok) {
      alert('Payouts saved successfully!');
      if (onClose) onClose();
    } else {
      const error = await response.json();
      alert('Failed to save payouts: ' + error.error);
    }
  } catch (error) {
    console.error('Error saving payouts:', error);
    alert('Failed to save payouts');
  }
};

  const totalPercentage = payouts.reduce((sum, p) => sum + (p.percentage || 0), 0);
  const totalAmount = payouts.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        padding: '30px',
        borderRadius: '8px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h2>Configure Payouts</h2>
          <button onClick={onClose} style={{ fontSize: '24px', border: 'none', background: 'none', cursor: 'pointer' }}>×</button>
        </div>

        <div className="card" style={{ marginBottom: '20px' }}>
        <h4>Prize Pool: ${(parseFloat(prizePool) || 0).toFixed(2)}</h4>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={usePercentages}
                onChange={(e) => setUsePercentages(e.target.checked)}
              />
              {' '}Use Percentages (auto-calculate amounts)
            </label>
            {usePercentages && (
              <button
                className="btn"
                onClick={calculateFromPercentages}
                style={{ marginLeft: '10px' }}
              >
                Recalculate Amounts
              </button>
            )}
          </div>
        </div>

        <div className="card">
          <h4>Payout Structure</h4>
          <table className="table">
            <thead>
              <tr>
                <th>Position</th>
                {usePercentages && <th>Percentage (%)</th>}
                <th>Amount ($)</th>
                <th>Label</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((payout, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={payout.position}
                      onChange={(e) => updatePayout(index, 'position', e.target.value)}
                      style={{ width: '80px' }}
                    />
                  </td>
                  {usePercentages && (
                    <td>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        value={payout.percentage}
                        onChange={(e) => updatePayout(index, 'percentage', e.target.value)}
                        style={{ width: '100px' }}
                      />
                    </td>
                  )}
                  <td>
                    <input
                      type="number"
                      step="1"
                      className="form-control"
                      value={(parseFloat(payout.amount) || 0).toFixed(2)}
                      onChange={(e) => updatePayout(index, 'amount', e.target.value)}
                      readOnly={usePercentages}
                      style={{ width: '120px' }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Event Ticket"
                      value={payout.label || ''}
                      onChange={(e) => updatePayout(index, 'label', e.target.value)}
                      style={{ width: '150px' }}
                    />
                  </td>
                  <td>
                    <button
                      className="btn btn-danger"
                      onClick={() => removePlace(index)}
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 'bold', borderTop: '2px solid #ddd' }}>
                <td>Total:</td>
                {usePercentages && (
                  <td style={{ color: totalPercentage === 100 ? 'green' : 'red' }}>
                    {totalPercentage.toFixed(1)}%
                  </td>
                )}
                <td>${totalAmount.toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>

          {usePercentages && totalPercentage !== 100 && (
            <div style={{ color: 'red', marginTop: '10px' }}>
              Warning: Total percentage is {totalPercentage.toFixed(1)}%, should be 100%
            </div>
          )}

          <button
            className="btn"
            onClick={addPlace}
            style={{ marginTop: '10px' }}
          >
            + Add Place
          </button>
        </div>

        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-success" onClick={savePayouts}>Save Payouts</button>
        </div>
      </div>
    </div>
  );
};

export default PayoutManager;